import axios from 'axios';
import axiosInstance from './axiosInstance';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
export const UPLOADS_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace('/api', '');

const STREAM_SUBDOMAIN = process.env.REACT_APP_CLOUDFLARE_STREAM_SUBDOMAIN || '';
export function buildStreamIframeUrl(uid?: string): string {
  if (!uid || !STREAM_SUBDOMAIN) return '';
  return `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/iframe`;
}

// Token "live": associate_token (associato) o representative_token (rappresentante).
// Fix H6 ciclo 2: context-aware. Un utente puo\` avere entrambi i token (rappresentante
// dopo enter-studio, o profilo duale). Default = associate_token (pagina pubblica),
// ma chi naviga in /area-associativa preferisce il representative_token per mantenere
// l'identita\` da reporter/moderatore.
export const getLiveActorToken = (opts?: { preferRepresentative?: boolean }): string | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const ass = window.localStorage.getItem('associate_token');
  const rep = window.localStorage.getItem('representative_token');
  if (opts?.preferRepresentative) return rep || ass || null;
  // Default: se il path corrente e\` /area-associativa preferisci rep automaticamente.
  if (typeof window.location !== 'undefined'
      && window.location.pathname?.startsWith('/area-associativa')) {
    return rep || ass || null;
  }
  return ass || rep || null;
};
const liveAuthHeader = (): { Authorization: string } | {} => {
  const t = getLiveActorToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Fix M9 ciclo 2: recompute lazy lato FE del flag emoOnly (il backend lo salva snapshot
// al create — cambi futuri della regex lasciano i messaggi storici desincronizzati).
// Stessa logica del backend isEmoOnly (live.js).
const EMO_CHAR_RE = /[\p{Extended_Pictographic}\p{Regional_Indicator}\p{Emoji_Modifier}\u{20E3}\u{FE0E}\u{FE0F}\u{200D}\s]/u;
const EMO_ONLY_RE = new RegExp(`^${EMO_CHAR_RE.source}+$`, 'u');
const HAS_EMOJI_RE = /[\p{Extended_Pictographic}\p{Regional_Indicator}]/u;
export const isEmoOnlyClient = (text: string): boolean =>
  HAS_EMOJI_RE.test(text) && EMO_ONLY_RE.test(text);

export type LivePostKind = 'text' | 'photo' | 'video' | 'youtube' | 'link' | 'article';

export interface LiveMediaItem {
  url: string;
  mimeType?: string;
  width?: number;
  height?: number;
}

export interface LiveLinkMeta {
  domain?: string;
  title?: string;
  description?: string;
  thumbUrl?: string;
  platform?: string;
}

export interface LiveAuthor {
  representativeId?: string | { _id: string; nome?: string; cognome?: string; foto?: string | null };
  name?: string;
}

export interface LivePost {
  _id: string;
  liveEventId: string;
  kind: LivePostKind;
  text?: string;
  mediaUrl?: string;
  mediaItems?: LiveMediaItem[];
  streamUid?: string;
  streamStatus?: string;
  streamThumbnailUrl?: string;
  youtubeId?: string;
  linkUrl?: string;
  linkMeta?: LiveLinkMeta;
  contentId?: string | null;
  caption?: string;
  pinned?: boolean;
  author?: LiveAuthor;
  createdAt: string;
}

export interface LiveEvent {
  _id: string;
  title: string;
  slug: string;
  subtitle?: string;
  coverImage?: string | null;
  status: 'draft' | 'live' | 'paused' | 'ended';
  isPublic: boolean;
  chronologicalOrder?: boolean;
  chatAlwaysOpen?: boolean;
  linkedEventId?: any;
  startedAt?: string | null;
  accumulatedSeconds?: number;
  endedAt?: string | null;
  notifiedAt?: string | null;
}

export interface LiveViewerProfile {
  kind: 'associate' | 'representative' | 'public';
  name?: string;
  initials?: string;
  foto?: string | null;
  communityProfile?: {
    avatarType?: 'initials' | 'icon' | 'image';
    avatarColor?: string;
    avatarIcon?: string | null;
    avatarImage?: string | null;
  } | null;
}

export interface LiveViewersPayload {
  count: number;
  recent: LiveViewerProfile[];
}

export const REACTION_KEYS = ['like', 'love', 'clap', 'celebrate', 'wow', 'dislike', 'sad'] as const;
export type ReactionKey = typeof REACTION_KEYS[number];
export type ReactionCounts = Record<ReactionKey, number>;

export const REACTION_EMOJI: Record<ReactionKey, string> = {
  like: '👍', love: '❤️', clap: '👏', celebrate: '🎉', wow: '😮', dislike: '👎', sad: '😢'
};
export const REACTION_LABEL: Record<ReactionKey, string> = {
  like: 'Mi piace', love: 'Adoro', clap: 'Applauso', celebrate: 'Bravo', wow: 'Wow', dislike: 'Non mi piace', sad: 'Triste'
};

export const emptyReactionCounts = (): ReactionCounts =>
  REACTION_KEYS.reduce((acc, k) => { acc[k] = 0; return acc; }, {} as ReactionCounts);

export interface LiveChatMessage {
  _id: string;
  liveEventId: string;
  author: {
    userType: 'associate' | 'representative';
    associateId?: string;
    representativeId?: string;
    name: string;
    initials: string;
    communityProfile?: {
      avatarType?: 'initials' | 'icon' | 'image';
      avatarColor?: string;
      avatarIcon?: string;
      avatarImage?: string;
    } | null;
    foto?: string;
    /** Persona che ha scritto per lo studio: presente solo per i dipendenti (il titolare non lo ha). */
    attore?: { tipo: 'dipendente'; dipendenteId?: string; nome?: string } | null;
  };
  text: string;
  emoOnly?: boolean;
  status?: 'published' | 'deleted';
  createdAt: string;
}

export interface LiveReactionsSnapshot {
  event: ReactionCounts;
  posts: Record<string, ReactionCounts>;
  mine?: {
    event: ReactionKey | null;
    posts: Record<string, ReactionKey>;
  };
}

export async function fetchLive(slug: string): Promise<{
  liveEvent: LiveEvent;
  posts: LivePost[];
  reactions?: LiveReactionsSnapshot;
}> {
  const { data } = await axios.get(`${API_BASE}/live/${slug}`, { headers: { ...liveAuthHeader() } });
  return data;
}

// Paginazione storica: 'before' carica i precedenti (desc, default),
// 'after' carica i successivi (asc, usato in modalita cronologica).
export async function fetchMorePosts(
  slug: string,
  cursor: { before?: string; after?: string }
): Promise<LivePost[]> {
  const { data } = await axios.get(`${API_BASE}/live/${slug}/posts`, {
    params: cursor, headers: { ...liveAuthHeader() }
  });
  return data.posts;
}

// ===== Chat =====
export async function fetchChat(slug: string, before?: string): Promise<LiveChatMessage[]> {
  const { data } = await axios.get(`${API_BASE}/live/${slug}/chat`, {
    params: before ? { before } : {},
    headers: { ...liveAuthHeader() }
  });
  return data.messages || [];
}

export async function sendChatMessage(slug: string, text: string): Promise<LiveChatMessage> {
  const { data } = await axios.post(`${API_BASE}/live/${slug}/chat`, { text }, {
    headers: { ...liveAuthHeader() }
  });
  return data.message;
}

// ===== Reactions =====
export async function sendReaction(slug: string, emoji: ReactionKey, postId?: string | null): Promise<{
  action: 'added' | 'updated' | 'removed' | 'noop';
  emoji: ReactionKey;
  postId: string | null;
}> {
  const { data } = await axios.post(`${API_BASE}/live/${slug}/react`,
    { emoji, postId: postId || null },
    { headers: { ...liveAuthHeader() } }
  );
  return data;
}

// Snapshot reactions per N post (usato dopo loadOlder per riempire counts dei post storici).
// Fix M12: il backend ritorna anche `mine.posts` (la propria reazione per ciascun postId).
export async function fetchReactionsForPosts(slug: string, postIds: string[]): Promise<{
  event: ReactionCounts;
  posts: Record<string, ReactionCounts>;
  mine?: { posts: Record<string, ReactionKey> };
}> {
  if (postIds.length === 0) return { event: emptyReactionCounts(), posts: {}, mine: { posts: {} } };
  const { data } = await axios.get(`${API_BASE}/live/${slug}/reactions`, {
    params: { postIds: postIds.join(',') },
    headers: { ...liveAuthHeader() }
  });
  return data.reactions || { event: emptyReactionCounts(), posts: {}, mine: { posts: {} } };
}

export const adminLive = {
  list: () => axiosInstance.get('/admin/live').then(r => r.data.items),
  create: (payload: Partial<LiveEvent>) => axiosInstance.post('/admin/live', payload).then(r => r.data.item),
  update: (id: string, payload: Partial<LiveEvent>) => axiosInstance.put(`/admin/live/${id}`, payload).then(r => r.data.item),
  start: (id: string) => axiosInstance.post(`/admin/live/${id}/start`).then(r => r.data.item),
  pause: (id: string) => axiosInstance.post(`/admin/live/${id}/pause`).then(r => r.data.item),
  end: (id: string) => axiosInstance.post(`/admin/live/${id}/end`).then(r => r.data.item),
  remove: (id: string) => axiosInstance.delete(`/admin/live/${id}`),
  moderateDelete: (id: string, postId: string) => axiosInstance.delete(`/admin/live/${id}/posts/${postId}`),
  listPosts: (id: string) => axiosInstance.get(`/admin/live/${id}/posts`).then(r => r.data.posts),
  listChat: (id: string) => axiosInstance.get(`/admin/live/${id}/chat`).then(r => r.data.messages),
  moderateChatDelete: (id: string, msgId: string) => axiosInstance.delete(`/admin/live/${id}/chat/${msgId}`),
  notify: (id: string, body: { sendEmail?: boolean; force?: boolean }) =>
    axiosInstance.post(`/admin/live/${id}/notify`, body).then(r => r.data),
};
